// Preview of an announcement as a firm user sees it in the Portal: in the bell, as a banner across the top of
// the page, or as the pop-up when they sign in. It draws the unsaved text from the editor. Nothing in it is a
// real control: the link and buttons are drawn, not live, and no receipt is recorded.
import { useState } from 'react';
import { Bell, CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { Banner, Button, Dialog, cx, type BannerTone } from '../../ui';

export type PreviewAnnouncement = {
  title: string; body: string; link_path: string | null; delivery: 'inbox' | 'banner' | 'popup';
  tone: 'info' | 'success' | 'warning'; release_name: string | null; audience: string; when: string;
};
type View = 'bell' | 'banner' | 'popup';
const VIEW: Record<View, string> = { bell: 'Bell', banner: 'Banner', popup: 'Sign-in pop-up' };
const BANNER_TONE: Record<PreviewAnnouncement['tone'], BannerTone> = { info: 'info', success: 'success', warning: 'warn' };
const ICON = { info: Info, success: CircleCheck, warning: CircleAlert };
const ICON_TONE = { info: 'bg-brand-soft text-brand', success: 'bg-ok-bg text-ok-fg', warning: 'bg-warn-tint text-warn-fg' };

/** Every announcement sits in the bell; a banner or pop-up shows there too. */
export const previewViews = (delivery: PreviewAnnouncement['delivery']): View[] =>
  delivery === 'banner' ? ['banner', 'bell'] : delivery === 'popup' ? ['popup', 'bell'] : ['bell'];

export function AnnouncementPreview({ open, onClose, announcement }: { open: boolean; onClose: () => void; announcement: PreviewAnnouncement }) {
  return (
    <Dialog open={open} title="Preview: what firm users see" onClose={onClose} wide
      footer={<Button variant="primary" size="sm" onClick={onClose}>Done</Button>}>
      <Frame a={announcement} />
    </Dialog>
  );
}

function Frame({ a }: { a: PreviewAnnouncement }) {
  const views = previewViews(a.delivery);
  const [view, setView] = useState<View>(views[0]!);
  const Icon = ICON[a.tone];
  const learnMore = a.link_path && (
    <span className="font-semibold text-brand underline underline-offset-2" title={`Opens ${a.link_path} in the Portal`}>Learn more</span>
  );
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-[13px] text-muted">{a.audience} · {a.when}</p>
      {views.length > 1 && (
        <div role="group" aria-label="Where it shows" className="flex flex-wrap gap-1.5">
          {views.map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
              className={cx('rounded-pill border px-3 py-1 text-[13px] font-semibold',
                view === v ? 'border-brand bg-brand-soft text-brand' : 'border-line-btn bg-white text-ink hover:bg-brand-tint')}>
              {VIEW[v]}
            </button>
          ))}
        </div>
      )}

      <div role="group" aria-label="Portal preview" className="relative min-h-[380px] overflow-hidden rounded-card border border-line bg-subtle">
        <div className="flex items-center justify-between border-b border-line bg-white px-4 py-2.5">
          <span className="text-[15px] font-bold text-ink">Vertekal Portal</span>
          <span className="relative flex items-center gap-3 text-muted">
            <span className={cx('relative rounded-lg p-1.5', view === 'bell' && 'bg-subtle text-ink')}>
              <Bell aria-hidden size={20} />
              <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-bad px-1 text-[10px] font-bold text-white">1</span>
            </span>
            <span className="h-7 w-7 rounded-full bg-line" />
          </span>
        </div>

        {view === 'banner' && (
          <div className="px-4 pt-3">
            <Banner tone={BANNER_TONE[a.tone]}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <span className="font-semibold">{a.title}</span>{' '}
                  <span className="whitespace-pre-wrap text-ink-2">{a.body}</span>
                  {learnMore && <> {learnMore}</>}
                </div>
                <X aria-hidden size={16} className="mt-0.5 flex-none opacity-70" />
              </div>
            </Banner>
          </div>
        )}

        {/* A stand-in for the page underneath. */}
        <div className="flex flex-col gap-3 p-4" aria-hidden>
          <div className="h-5 w-40 rounded bg-line" />
          <div className="grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-lg border border-line bg-white" />)}
          </div>
          <div className="h-28 rounded-lg border border-line bg-white" />
        </div>

        {view === 'bell' && (
          <div className="absolute right-3 top-12 w-[min(360px,calc(100%-24px))] overflow-hidden rounded-lg border border-line bg-white shadow-xl">
            <div className="border-b border-line px-4 py-2.5 text-sm font-semibold text-ink">Notifications</div>
            <div className="flex items-start gap-3 bg-brand-tint px-4 py-3">
              <span className={cx('mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full', ICON_TONE[a.tone])}><Icon aria-hidden size={15} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">From Vertekal{a.release_name ? ` · What’s new in ${a.release_name}` : ''}</div>
                <div className="text-sm font-semibold text-ink">{a.title}</div>
                <div className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-[13px] text-ink-2">{a.body}</div>
                {learnMore && <div className="mt-1 text-[13px]">{learnMore}</div>}
              </div>
              <X aria-hidden size={14} className="mt-1 flex-none text-muted" />
            </div>
            <div className="px-4 py-3 text-[13px] text-muted">Your other notifications show below.</div>
          </div>
        )}

        {view === 'popup' && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink/45 p-4">
            <div className="w-full max-w-[440px] rounded-card bg-white p-5 shadow-xl">
              <div className="flex items-start gap-3">
                <span className={cx('flex h-9 w-9 flex-none items-center justify-center rounded-full', ICON_TONE[a.tone])}><Icon aria-hidden size={18} /></span>
                <div className="min-w-0 flex-1">
                  {a.release_name && <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">What’s new in {a.release_name}</div>}
                  <div className="text-[17px] font-bold leading-snug text-ink">{a.title}</div>
                </div>
                <X aria-hidden size={16} className="mt-1 flex-none text-muted" />
              </div>
              <p className="mb-0 mt-3 max-h-[220px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-ink-2">{a.body}</p>
              <div className="mt-4 flex items-center justify-end gap-3 text-sm">
                {learnMore}
                <span className="rounded-lg bg-brand px-4 py-2 font-semibold text-white">Got it</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <p className="m-0 text-[12px] text-muted">Nothing here is sent or recorded. Links and buttons are shown, not live.</p>
    </div>
  );
}
